import { validUSPhoneNumberRegex } from '@/constants';

const MASKED_US_PHONE_REGEX = /\+1 \(\d{3}\) \d{3}-\d{4}$/;
const E164_US_PHONE_REGEX = /^\+1\d{10}$/;
/** Explicit non-US country code (+53, +44, +256, …). */
const NON_US_COUNTRY_CODE = /^\s*\+(?!1(?:\D|$))/;

type PhoneValidatorRule = {
  field: string;
  message: string;
  method: string | ((_value: string) => boolean);
  validWhen: boolean;
};

type PhoneValidatorOptions = {
  field: string;
  required?: boolean;
  /** Accept raw E.164 (`+15555555555`) in addition to masked input. */
  acceptE164?: boolean;
};

export const getPhoneDigits = (phone?: string | null) =>
  String(phone ?? '').replace(/\D/g, '');

/** Empty / country-code-only → null; otherwise E.164 (`+15555555555`). */
export const toE164Phone = (phone?: string | null): string | null => {
  const digits = getPhoneDigits(phone);
  return digits.length > 1 ? `+${digits}` : null;
};

const isNonUSPhone = (phone: string) => NON_US_COUNTRY_CODE.test(phone);

const isCompleteUSPhone = (phone: string, acceptE164: boolean) => {
  const maskedOk = MASKED_US_PHONE_REGEX.test(phone);
  const e164Ok = acceptE164 && E164_US_PHONE_REGEX.test(phone);
  return maskedOk || e164Ok;
};

const toMaskedUSPhone = (phone: string) => {
  if (MASKED_US_PHONE_REGEX.test(phone)) return phone;

  const digits = getPhoneDigits(phone);
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(
      7,
    )}`;
  }

  return phone;
};

export const getPhoneValidatorRules = ({
  field,
  required = true,
  acceptE164 = false,
}: PhoneValidatorOptions): PhoneValidatorRule[] => {
  const rules: PhoneValidatorRule[] = [];

  if (required) {
    rules.push({
      field,
      message: 'Enter your phone number.',
      method: 'isEmpty',
      validWhen: false,
    });
  }

  rules.push(
    {
      field,
      message: 'Phone number must be a US number (+1).',
      method: (phone: string) => {
        if (!required && !toE164Phone(phone)) return true;
        return !isNonUSPhone(phone);
      },
      validWhen: true,
    },
    {
      field,
      message: 'The phone format is incorrect.',
      method: (phone: string) => {
        if (!required && !toE164Phone(phone)) return true;
        // Non-US already covered by the previous rule.
        if (isNonUSPhone(phone)) return true;
        return isCompleteUSPhone(phone, acceptE164);
      },
      validWhen: true,
    },
    {
      field,
      message: 'Phone number must be a valid US number.',
      method: (phone: string) => {
        if (!required && !toE164Phone(phone)) return true;
        if (isNonUSPhone(phone) || !isCompleteUSPhone(phone, acceptE164)) {
          return true;
        }

        const value = acceptE164 ? toMaskedUSPhone(phone) : phone;
        return validUSPhoneNumberRegex.test(value);
      },
      validWhen: true,
    },
  );

  return rules;
};
